/**
 * Decides, on the user's machine, which pages of a specification book or a drawing set are
 * worth sending to be analysed. Only those pages' text leaves the browser.
 *
 * Specs: every page of a section carries a running header with the section code and the page
 * within it ("27 51 23 -3   CENTRAL SOUND SYSTEM"). That header is the signal; the table of
 * contents is skipped (it has no page numbers and its codes disagree with the sections in
 * places) and a "Section 26 05 00" in the body is a cross-reference, not a start.
 * Drawings: the sheet number in the title block, plus the legend and technology sheets.
 */

import type { PageText } from "@plan-forge/speclens-contract";

export interface RawPage {
  /** 1-based PDF page number. */
  readonly page: number;
  /** The page's text, one line per "\n". */
  readonly text: string;
}

export interface LocatedPage extends RawPage {
  readonly section?: string;
  readonly sectionTitle?: string;
  /** The page number printed in the header, 0 when the page has none. */
  readonly sectionPage: number;
}

export interface SectionRun {
  readonly section: string;
  readonly title: string;
  readonly firstPage: number;
  readonly lastPage: number;
  readonly pages: readonly number[];
}

const HEADER = /(?<![\d.\/])(\d{2}) ?(\d{2}) ?(\d{2})(?:\.(\d{2}))? ?[-–] ?(\d{1,3})(?![\d"'”.])/;
const HEADER_PAGE_WORD = /(?<![\d.\/])(\d{2}) ?(\d{2}) ?(\d{2})(?:\.(\d{2}))?\s*[-–]?\s*(?:page|pg\.?)\s*(\d{1,3})\b/i;
const START = /^\s*SECTION\s+(\d{2}) ?(\d{2}) ?(\d{2})(?:\.(\d{2}))?/m;
const CODE_IN_TOC = /\b\d{2} \d{2} \d{2}\b/g;

const EDGE_LINES = 6;
const MAX_HEADER_LINE = 80;

/**
 * "27 51 23 Page 3" as a running header or footer: a short line among the first or last few. A
 * sentence that says "see 27 41 00, page 3" is a cross-reference and never one of these.
 */
function pageWordHeader(text: string): RegExpExecArray | null {
  const lines = text.split("\n").map((line) => line.replace(/\s+/g, " ").trim()).filter((line) => line !== "");
  for (const line of [...lines.slice(0, EDGE_LINES), ...lines.slice(-EDGE_LINES)]) {
    if (line.length > MAX_HEADER_LINE) continue;
    const match = HEADER_PAGE_WORD.exec(line);
    if (match !== null && !/\b(?:see|refer|per|in|of|under)\b/i.test(line.slice(0, match.index))) return match;
  }
  return null;
}

function formatCode(a: string, b: string, c: string, d: string | undefined): string {
  return `${a} ${b} ${c}${d === undefined ? "" : `.${d}`}`;
}

function isBlank(flat: string): boolean {
  return flat.length < 40 || /intentionally\s+left\s+blank/i.test(flat);
}

function isContentsPage(flat: string): boolean {
  return /TABLE\s+OF\s+CONTENTS/i.test(flat.slice(0, 300)) && (flat.match(CODE_IN_TOC)?.length ?? 0) > 8;
}

function titleFrom(text: string): string {
  const line = text.split("\n").find((candidate) => HEADER.test(candidate.replace(/\s+/g, " ")) || pageWordHeader(candidate) !== null);
  if (line !== undefined) {
    const flat = line.replace(/\s+/g, " ");
    const match = HEADER.exec(flat) ?? pageWordHeader(flat);
    if (match !== null) {
      const rest = flat.slice(match.index + match[0].length).trim();
      if (/^[A-Z][A-Z0-9 ,&\/()'.\-–]{2,70}$/.test(rest)) return rest;
    }
  }
  const start = /^\s*SECTION\s+\d{2} ?\d{2} ?\d{2}(?:\.\d{2})?\s*[-–:]?\s*([^\n]{3,70})/m.exec(text);
  return start?.[1]?.trim() ?? "";
}

/**
 * Attributes each page to a specification section.
 *
 * A page belongs to the section its header names. A page with no header continues the section
 * above it until that section has said "END OF SECTION"; blank pages, contents pages and
 * everything before the first section (cover, geotechnical report) belong to no section.
 */
export function locateSections(pages: readonly RawPage[]): readonly LocatedPage[] {
  const located: LocatedPage[] = [];
  let currentSection: string | undefined;
  let currentTitle = "";
  let closed = true;

  for (const raw of [...pages].sort((a, b) => a.page - b.page)) {
    const flat = raw.text.replace(/\s+/g, " ").trim();
    if (isBlank(flat) || isContentsPage(flat)) {
      located.push({ ...raw, sectionPage: 0 });
      continue;
    }

    const head = flat.slice(0, 600);
    const tail = flat.slice(-300);
    const header = HEADER.exec(head) ?? HEADER.exec(tail) ?? pageWordHeader(raw.text);
    const headerCode =
      header !== null && Number(header[1]) < 50 ? formatCode(header[1] ?? "", header[2] ?? "", header[3] ?? "", header[4]) : undefined;
    const marker = START.exec(raw.text.slice(0, 900));
    const markerCode =
      marker !== null && Number(marker[1]) < 50 ? formatCode(marker[1] ?? "", marker[2] ?? "", marker[3] ?? "", marker[4]) : undefined;

    const code = headerCode ?? markerCode;
    if (code !== undefined) {
      const title = titleFrom(raw.text);
      if (code !== currentSection) currentTitle = title;
      else if (currentTitle === "") currentTitle = title;
      currentSection = code;
      closed = /END\s+OF\s+SECTION/i.test(flat.slice(-500));
      located.push({
        ...raw,
        section: code,
        sectionTitle: currentTitle,
        sectionPage: header === null ? 1 : Number(header[5]),
      });
      continue;
    }

    if (currentSection !== undefined && !closed) {
      closed = /END\s+OF\s+SECTION/i.test(flat.slice(-500));
      located.push({ ...raw, section: currentSection, sectionTitle: currentTitle, sectionPage: 0 });
      continue;
    }
    located.push({ ...raw, sectionPage: 0 });
  }
  return located;
}

export function sectionRuns(pages: readonly LocatedPage[]): readonly SectionRun[] {
  const runs: { section: string; title: string; pages: number[] }[] = [];
  for (const page of pages) {
    if (page.section === undefined) continue;
    const last = runs.at(-1);
    if (last !== undefined && last.section === page.section) last.pages.push(page.page);
    else runs.push({ section: page.section, title: page.sectionTitle ?? "", pages: [page.page] });
  }
  return runs.map((run) => ({
    section: run.section,
    title: run.title,
    firstPage: run.pages[0] ?? 0,
    lastPage: run.pages.at(-1) ?? 0,
    pages: run.pages,
  }));
}

export interface SpecSelection {
  /** Two-digit division codes to keep, e.g. ["00", "01", "09", "26", "27"]. */
  readonly divisions: readonly string[];
  /** For Division 26, keep only the sections that touch audio-visual wiring and devices. */
  readonly limitDivision26: boolean;
}

export const DEFAULT_SPEC_SELECTION: SpecSelection = {
  divisions: ["00", "01", "09", "26", "27"],
  limitDivision26: true,
};

const DIVISION_26_AV = /^(?:26 05 (?:00|13|26|33|36|53)|26 27 26)/;

export function wantsSection(section: string, selection: SpecSelection): boolean {
  const division = section.slice(0, 2);
  if (!selection.divisions.includes(division)) return false;
  if (division === "26" && selection.limitDivision26) return DIVISION_26_AV.test(section);
  return true;
}

export function selectSpecPages(pages: readonly LocatedPage[], selection: SpecSelection): readonly LocatedPage[] {
  return pages.filter((page) => page.section !== undefined && wantsSection(page.section, selection));
}

export function toPageText(page: LocatedPage): PageText {
  return {
    locator: `Page ${page.page}`,
    page: page.page,
    ...(page.section !== undefined && { section: page.section }),
    ...(page.sectionTitle !== undefined && page.sectionTitle !== "" && { sectionTitle: page.sectionTitle }),
    ...(page.sectionPage > 0 && { sectionPage: page.sectionPage }),
    text: page.text,
  };
}

export interface TextItem {
  readonly text: string;
  /** Font size; the sheet number is set far larger than anything else in the title block. */
  readonly size: number;
}

const SHEET = /^[A-Z]{1,3}\d{1,3}(?:\.\d{1,2})?(?:-[A-Z0-9]{1,2})?[A-Z]?$/;

/** The sheet number: the largest text on the page that is shaped like one, at least 20 pt. */
export function findSheetNumber(items: readonly TextItem[]): string | undefined {
  let best: TextItem | undefined;
  for (const item of items) {
    const text = item.text.trim();
    if (!SHEET.test(text) || item.size < 20) continue;
    if (best === undefined || item.size > best.size) best = { text, size: item.size };
  }
  return best?.text;
}

export interface DrawingPage extends RawPage {
  readonly sheet?: string;
}

const SHEET_FAMILIES = /^(?:T|TS|G|E0|A7)/i;
const AV_WORDS = /\b(?:PA\s+SPEAKER|CLASSROOM\s+SOUND|PAGING|INTERCOM|A\/V|AUDIO[- ]VISUAL|PUBLIC\s+ADDRESS|SOUND\s+SYSTEM)\b/i;

/**
 * The sheets worth reading: technology, general notes, the electrical legend and the
 * reflected ceiling plans. A set with no readable sheet numbers falls back to pages that
 * name an audio-visual system outright.
 */
export function defaultSheetSelection(pages: readonly DrawingPage[]): readonly number[] {
  const numbered = pages.filter((page) => page.sheet !== undefined);
  if (numbered.length >= Math.max(3, pages.length / 4)) {
    return numbered.filter((page) => SHEET_FAMILIES.test(page.sheet ?? "")).map((page) => page.page);
  }
  return pages.filter((page) => AV_WORDS.test(page.text)).map((page) => page.page);
}

export function toDrawingPageText(page: DrawingPage): PageText {
  return {
    locator: page.sheet ?? `Page ${page.page}`,
    page: page.page,
    ...(page.sheet !== undefined && { sheet: page.sheet }),
    text: page.text,
  };
}
